import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col, Alert, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import config from '../../config';
import { loggedInUserId } from '../../utils/loggedInUserDetails';

const PurchaseRequestModal = ({ show, onHide, product, onSuccess }) => {
  const [formData, setFormData] = useState({
    quantity: '',
    requiredDate: '',
    priority: 'medium',
    reason: 'low stock',
    notes: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (product && show) {
      // Auto-fill form when product is selected
      // Handle both nested product structure (product.product) and direct product structure
      const productData = product.product || product;
      const suggestedQuantity = Math.max(
        (productData?.bufferStock || 0) - (product.totalAvailableStock || 0),
        productData?.bufferStock || 10
      );

      setFormData({
        quantity: suggestedQuantity,
        requiredDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 2 days from now
        priority: 'medium',
        reason: 'low stock',
        notes: `Stock replenishment for ${productData?.productName || 'Unknown Product'} - Current stock: ${product.totalAvailableStock || 0}, Buffer stock: ${productData?.bufferStock || 0}`
      });
    } else {
      // Reset form
      setFormData({
        quantity: '',
        requiredDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        priority: 'medium',
        reason: 'low stock',
        notes: ''
      });
    }

    // Reset loading states when modal opens/closes
    if (!show) {
      setLoading(false);
      setIsSubmitting(false);
    }
  }, [product, show]);

  const handleSubmit = async () => {
    if (!product || !formData.quantity || isSubmitting) {
      if (!product || !formData.quantity) {
        toast.error('Please fill in all required fields');
      }
      return;
    }

    try {
      setIsSubmitting(true);
      setLoading(true);

      // Get user ID and validate
      const userInfo = loggedInUserId();
      if (!userInfo || !userInfo.userId) {
        toast.error('User not authenticated. Please login again.');
        return;
      }

      const productData = product.product || product;
      if (!productData?._id) {
        toast.error('Invalid product data. Please try again.');
        return;
      }

      const purchaseRequestData = {
        required_by_date: formData.requiredDate,
        reason: formData.reason,
        requested_by: userInfo.userId,
        priority: formData.priority,
        notes: formData.notes,
        items: [{
          product: productData._id,
          approx_quantity: parseInt(formData.quantity),
          quantity_unit: productData?.unitId?.unitCode || 'PCS',
          quality: '' // Can be extended later if needed
        }]
      };

      console.log('Sending purchase request data:', purchaseRequestData); // Debug log

      const response = await axios.post(`${config.API_BASE_URL}/purchase-requests`, purchaseRequestData);
      
      if (response.status === 201) {
        const productData = product.product || product;
        toast.success(`Purchase request created successfully for ${productData?.productName || 'Unknown Product'}`);

        // First trigger the callback
        if (onSuccess) {
          await onSuccess();
        }

        // Then close modal with a small delay to prevent DOM conflicts
        setTimeout(() => {
          onHide();
        }, 500);
      }
    } catch (error) {
      console.error('Error creating purchase request:', error);
      toast.error(error.response?.data?.message || 'Failed to create purchase request');
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!loading && !isSubmitting) {
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Create Purchase Request</Modal.Title>
      </Modal.Header>
      
      <Form>
        <Modal.Body>
          {product && (() => {
            const productData = product.product || product;
            return (
              <Alert variant="info" className="mb-3">
                <Row>
                  <Col md={8}>
                    <strong>{productData?.productName || 'Unknown Product'}</strong>
                    <br />
                    <small className="text-muted">
                      Code: {productData?.productCode || 'N/A'} |
                      Current Stock: {product.totalAvailableStock || 0} |
                      Buffer Stock: {productData?.bufferStock || 0}
                    </small>
                  </Col>
                  <Col md={4} className="text-end">
                    <Badge bg={product.totalAvailableStock < (productData?.bufferStock || 0) ? 'danger' : 'success'}>
                      {product.totalAvailableStock < (productData?.bufferStock || 0) ? 'Low Stock' : 'Normal Stock'}
                    </Badge>
                  </Col>
                </Row>
              </Alert>
            );
          })()}

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Required Quantity <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                  min="1"
                  required
                />
                <Form.Text className="text-muted">
                  Unit: {(() => {
                    if (!product) return 'PCS';
                    const productData = product.product || product;
                    return productData?.unitId?.unitCode || 'PCS';
                  })()}
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Required By Date <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="date"
                  value={formData.requiredDate}
                  onChange={(e) => setFormData({...formData, requiredDate: e.target.value})}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Priority</Form.Label>
                <Form.Select
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: e.target.value})}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Reason</Form.Label>
                <Form.Select
                  value={formData.reason}
                  onChange={(e) => setFormData({...formData, reason: e.target.value})}
                >
                  <option value="low stock">Low Stock</option>
                  <option value="urgent requirement">Urgent Requirement</option>
                  <option value="new project">New Project</option>
                  <option value="maintenance">Maintenance</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Notes / Specifications</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Additional notes, specifications, or requirements..."
            />
          </Form.Group>
        </Modal.Body>
        
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={loading || isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={loading || isSubmitting}>
            {loading || isSubmitting ? 'Submitting...' : 'Create Purchase Request'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default PurchaseRequestModal;
